import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import {
  isLoadingUrlEntries,
  selectUrlEntriesSortedByUpdatedAt,
} from "../selectors";
import { fetchUrlEntries } from "../thunks.js";

const UrlEntries = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const pageSize = 10;

  const urlEntries = useSelector(selectUrlEntriesSortedByUpdatedAt);
  const isLoading = useSelector(isLoadingUrlEntries);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchUrlEntries({ page, pageSize }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      console.error("Error fetching software packages:", err);
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const mapUrlEntryToTableRow = (entry) => {
    if (!entry.id || !entry.name) return null;
    return (
      <tr key={`urlentry-${entry.id}`}>
        <td>
          <Link to={`/url-entry/url-entry/edit/${entry.id}`}>{entry.name}</Link>
        </td>
        <td>{entry.url}</td>
        <td>{entry.browsingType}</td>
      </tr>
    );
  };

  const dismissError = () => setError(null);

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {intl.formatMessage(genericTranslations.errorMessage)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      {isLoading && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && urlEntries.length === 0 && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {!isLoading && urlEntries.length > 0 && (
        <Table responsive>
          <thead>
            <tr>
              <td>{intl.formatMessage(genericTranslations.name)}</td>
              <td>URL</td>
              <td>Browsetype</td>
            </tr>
          </thead>
          <tbody>{urlEntries.map(mapUrlEntryToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter
        actionPath="/url-entry/url-entry/add"
        onSelectPage={onSelectPage}
        page={page}
        pages={pages}
      />
    </div>
  );
};

export default UrlEntries;
